import { createSlice } from "@reduxjs/toolkit";
import { parseTimesheetData } from "./parser";
import {
  approvePayRollRate,
  getAllCandidateTimesheetCentresThunk,
  getApprovalTimesheetThunk,
  getCandidateActiveTimeSheet,
  getCandidatePendingTimesheetThunk,
  getCandidatesForExportThunk,
  getCandidatesThunk,
  getCandidateTimesheetHistoryThunk,
  getClientTimesheetPendingApprovalsThunk,
  getPayrolPendingTimesheetThunk,
  getPendingTimesheetThunk,
  getTimesheetHistoryThunk,
  submitCandidateTimeSheetThunk,
  updateTimesheetStatusById,
} from "./thunks";
import { ITimesheetData, ITimesheetState } from "./types";

const initialState: ITimesheetState = {
  inProgress: false,
  error: "",
  allCandidates: [],
  pendingTimesheets: [],
  pendingPagination: null,
  approvalTimesheets: [],
  approvalPagination: null,
  historyTimesheets: [],
  historyPagination: null,
  totalApprovalTimesheet: 0,
  totalPendingTimesheet: 0,
  totalPendingPayrollTimesheet: 0,
  candidatehistoryTimesheets: null,
  allCentersList: [],
  candidateLogTimeSheet: [],
  candidateActiveTimesheetList: null,
  totalCandidateActiveTimeSheet: null,
  allCandidatesForExportTimesheet: [],
  clientPendingTimesheetList: [],
  totalClientPendingTimesheetApproval: 0,
};

const timesheetSlice = createSlice({
  name: "timesheet",
  initialState,
  reducers: {
    resetTimesheetErrorAction: (state) => {
      state.error = null;
    },
    setCandidateLogTimesheetList: (state, action) => {
      state.candidateLogTimeSheet = action.payload;
    },
    setCandidateActiveTimesheetList: (state, action) => {
      state.candidateActiveTimesheetList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCandidatesThunk.pending, (state) => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(getCandidatesThunk.fulfilled, (state, action) => {
        state.inProgress = false;
        state.allCandidates = action.payload.candidates
          .map((candidate) => ({
            ...candidate,
            full_name: `${candidate.first_name} ${candidate.last_name}`.trim(),
          }))
          .sort((a, b) => a.full_name.localeCompare(b.full_name));
      })
      .addCase(getCandidatesThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(getCandidatesForExportThunk.pending, (state) => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(getCandidatesForExportThunk.fulfilled, (state, action) => {
        state.inProgress = false;
        state.allCandidatesForExportTimesheet = action.payload.candidates
          .map((candidate) => ({
            ...candidate,
            full_name: `${candidate.first_name} ${candidate.last_name}`.trim(),
          }))
          .sort((a, b) => a.full_name.localeCompare(b.full_name));
      })
      .addCase(getCandidatesForExportThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.payload || action.error.message;
      })
      // Timesheet Pending
      .addCase(getPendingTimesheetThunk.pending, (state) => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(getPendingTimesheetThunk.fulfilled, (state, action) => {
        state.inProgress = false;
        const rawData = Array.isArray(action.payload.timesheets?.data) 
          ? action.payload.timesheets.data 
          : [];
        let timesheetList: ITimesheetData[] = [];
        rawData.map((item) => {
          const parsed = parseTimesheetData(item);
          if (parsed) {
            timesheetList.push(parsed);
          }
        });
        state.pendingTimesheets = timesheetList;
        state.totalPendingTimesheet =
          action.payload.timesheet_pending_total || 0;
        state.totalApprovalTimesheet =
          action.payload.approval_pending_total || 0;
        state.pendingPagination = {
          current_page: action.payload.timesheets.current_page,
          per_page: action.payload.timesheets.per_page,
          total: action.payload.timesheets.total,
          last_page: action.payload.timesheets.last_page,
          next_page_url: action.payload.timesheets.next_page_url,
          prev_page_url: action.payload.timesheets.prev_page_url,
          links: action.payload.timesheets.links,
        };
        state.totalPendingPayrollTimesheet =
          action.payload.payroll_pending_total || 0;
        state.totalOverDueTimesheetCount = action.payload.overdue_timesheet_count || 0;
        state.totalPendingTimesheetCount = action.payload.pending_timesheet_count || 0;
      })
      .addCase(getPendingTimesheetThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.error.message;
      })
      // Approval pending
      .addCase(getApprovalTimesheetThunk.pending, (state) => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(getApprovalTimesheetThunk.fulfilled, (state, action) => {
        state.inProgress = false;
        const rawData = Array.isArray(action.payload.timesheets?.data) 
          ? action.payload.timesheets.data 
          : [];
        let timesheetList: ITimesheetData[] = [];
        rawData.map((item) => {
          const parsed = parseTimesheetData(item);
          if (parsed) {
            timesheetList.push(parsed);
          }
        });
        state.approvalTimesheets = timesheetList;
        state.totalPendingTimesheet =
          action.payload.timesheet_pending_total || 0;
        state.totalApprovalTimesheet =
          action.payload.approval_pending_total || 0;
        state.totalClientPendingTimesheetApproval =
          action.payload.approval_pending_total || 0;
        state.approvalPagination = {
          current_page: action.payload.timesheets.current_page,
          per_page: action.payload.timesheets.per_page,
          total: action.payload.timesheets.total,
          last_page: action.payload.timesheets.last_page,
          next_page_url: action.payload.timesheets.next_page_url,
          prev_page_url: action.payload.timesheets.prev_page_url,
          links: action.payload.timesheets.links,
        };
        state.totalOverDueTimesheetCount = action.payload.overdue_timesheet_count || 0;
        state.totalPendingTimesheetCount = action.payload.pending_timesheet_count || 0;
      })
      .addCase(getApprovalTimesheetThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.error.message;
      })
      // History
      .addCase(getTimesheetHistoryThunk.pending, (state) => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(getTimesheetHistoryThunk.fulfilled, (state, action) => {
        state.inProgress = false;
        const rawData = Array.isArray(action.payload.timesheets?.data) 
          ? action.payload.timesheets.data 
          : [];
        let timesheetList: ITimesheetData[] = [];
        rawData.map((item) => {
          const parsed = parseTimesheetData(item);
          if (parsed) {
            timesheetList.push(parsed);
          }
        });
        state.historyTimesheets = timesheetList;
        state.totalPendingTimesheet =
          action.payload.timesheet_pending_total || 0;
        state.totalApprovalTimesheet =
          action.payload.approval_pending_total || 0;
        state.historyPagination = {
          current_page: action.payload.timesheets.current_page,
          per_page: action.payload.timesheets.per_page,
          total: action.payload.timesheets.total,
          last_page: action.payload.timesheets.last_page,
          next_page_url: action.payload.timesheets.next_page_url,
          prev_page_url: action.payload.timesheets.prev_page_url,
          links: action.payload.timesheets.links,
        };
        state.totalPendingPayrollTimesheet = 
          action.payload.payroll_pending_total || 0;
        state.totalOverDueTimesheetCount = action.payload.overdue_timesheet_count || 0;
        state.totalPendingTimesheetCount = action.payload.pending_timesheet_count || 0;
      })
      .addCase(getTimesheetHistoryThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.error.message;
      })
      // Super admin pending payroll
      .addCase(getPayrolPendingTimesheetThunk.pending, (state) => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(getPayrolPendingTimesheetThunk.fulfilled, (state, action) => {
        state.inProgress = false;
        const rawData = Array.isArray(action.payload.timesheets?.data) 
          ? action.payload.timesheets.data 
          : [];
        let timesheetList: ITimesheetData[] = [];
        rawData.map((item) => {
          const parsed = parseTimesheetData(item);
          if (parsed) {
            timesheetList.push(parsed);
          }
        });
        state.pendingTimesheets = timesheetList;
        state.totalPendingTimesheet =
          action.payload.timesheet_pending_total || 0;
        state.totalApprovalTimesheet =
          action.payload.approval_pending_total || 0;
        state.totalPendingPayrollTimesheet =
          action.payload.payroll_pending_total || 0;
        state.pendingPagination = {
          current_page: action.payload.timesheets.current_page,
          per_page: action.payload.timesheets.per_page,
          total: action.payload.timesheets.total,
          last_page: action.payload.timesheets.last_page,
          next_page_url: action.payload.timesheets.next_page_url,
          prev_page_url: action.payload.timesheets.prev_page_url,
          links: action.payload.timesheets.links,
        };
        state.totalOverDueTimesheetCount = action.payload.overdue_timesheet_count || 0;
        state.totalPendingTimesheetCount = action.payload.pending_timesheet_count || 0;
      })
      .addCase(getPayrolPendingTimesheetThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.error.message;
      })
      .addCase(updateTimesheetStatusById.pending, (state) => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(updateTimesheetStatusById.fulfilled, (state, action) => {
        state.inProgress = false;

        const updatedId = action.meta.arg.id;
        state.approvalTimesheets = state.approvalTimesheets.filter(
          (ts) => ts.id !== updatedId
        );

        state.totalApprovalTimesheet = Math.max(
          0,
          state.totalApprovalTimesheet - 1
        );
      })
      .addCase(updateTimesheetStatusById.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.payload || action.error.message;
      })

      .addCase(getCandidateTimesheetHistoryThunk.pending, (state) => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(getCandidateTimesheetHistoryThunk.fulfilled, (state, action) => {
        state.inProgress = false;
        state.totalCandidateActiveTimeSheet =
          action.payload.data.approval_pending_total;
        state.candidatehistoryTimesheets = action.payload.data.timesheets;
      })
      .addCase(getCandidateTimesheetHistoryThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.error.message;
      })

      .addCase(getAllCandidateTimesheetCentresThunk.pending, (state) => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(
        getAllCandidateTimesheetCentresThunk.fulfilled,
        (state, action) => {
          state.inProgress = false;
          state.allCentersList = [];
          state.allCentersList = action.payload.data.centres;
        }
      )
      .addCase(
        getAllCandidateTimesheetCentresThunk.rejected,
        (state, action) => {
          state.inProgress = false;
          state.error = action.error.message;
        }
      );

    builder.addCase(getCandidatePendingTimesheetThunk.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getCandidatePendingTimesheetThunk.fulfilled, (state) => {
      state.inProgress = false;
    });
    builder.addCase(
      getCandidatePendingTimesheetThunk.rejected,
      (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      }
    );

    builder.addCase(submitCandidateTimeSheetThunk.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(submitCandidateTimeSheetThunk.fulfilled, (state) => {
      state.inProgress = false;
    });
    builder.addCase(submitCandidateTimeSheetThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    builder.addCase(getCandidateActiveTimeSheet.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getCandidateActiveTimeSheet.fulfilled, (state, action) => {
      state.inProgress = false;
      state.totalCandidateActiveTimeSheet =
        action.payload.approval_pending_total || 0;
    });
    builder.addCase(getCandidateActiveTimeSheet.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });
    
    builder.addCase(approvePayRollRate.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(approvePayRollRate.fulfilled, (state, action) => {
      state.inProgress = false;
    });
    builder.addCase(approvePayRollRate.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    builder.addCase(getClientTimesheetPendingApprovalsThunk.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getClientTimesheetPendingApprovalsThunk.fulfilled, (state, action) => {
      state.inProgress = false;
      const rawData = Array.isArray(action.payload.timesheets) 
        ? action.payload.timesheets 
        : [];
      let timesheetList: ITimesheetData[] = [];
      rawData.map((item: any) => {
        const parsed = parseTimesheetData(item);
        if (parsed) {
          timesheetList.push(parsed);
        }
      });
      state.clientPendingTimesheetList = timesheetList;
    });
    builder.addCase(getClientTimesheetPendingApprovalsThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });
  },
});

export const {
  resetTimesheetErrorAction,
  setCandidateLogTimesheetList,
  setCandidateActiveTimesheetList,
} = timesheetSlice.actions;

export default timesheetSlice.reducer;
